import React, { useEffect } from "react";
import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import {
  MapPin, Phone, ArrowRight, ShieldCheck, Check, Monitor, LifeBuoy, Mail, Wifi, ShieldAlert, CreditCard, Tv, HardDrive, Home, PhoneCall, Network, Shield, Satellite,
} from "lucide-react";
import SEO from "../components/SEO";
import { regions, services, PHONE, PHONE_TEL } from "../data/content";
import { suburbsBySlug, suburbsByRegion } from "../data/suburbs";
import { getSuburbFaqs } from "../data/suburbFaqs";

const iconMap = { Monitor, LifeBuoy, Mail, Wifi, ShieldAlert, ShieldCheck, CreditCard, Tv, HardDrive, Home, PhoneCall, Network, Shield, Satellite };

export function Suburbs() {
  return (
    <>
      <SEO
        title="Adelaide Suburbs We Cover | Mobile IT, Computer Repairs & Cyber Security"
        description="Tech Emergency covers 58+ Adelaide suburbs across North, East, South, West and the CBD. On-site computer repairs, WiFi rescue, cyber security and IT support."
        path="/suburbs"
      />
      <section className="pt-6 sm:pt-12 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ Coverage</div>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-none mb-6">
            We come to you<br />— across our service area.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
            58 prioritised Adelaide suburbs across North, East, South, West and the CBD. If yours isn&apos;t listed, call us — we likely still cover it.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {regions.map((r) => (
            <div key={r.name} data-testid={`region-block-${r.name.toLowerCase().replace(/\s|&/g, "")}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <MapPin size={20} style={{ color: "var(--te-primary)" }} />
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{r.name}</h2>
                </div>
                <span className="te-overline text-[10px]">{(suburbsByRegion[r.name] || []).length} suburbs</span>
              </div>
              {r.note && (
                <p className="text-sm text-zinc-400 mb-5 -mt-2" data-testid={`region-note-${r.name.toLowerCase()}`}>
                  {r.note}
                </p>
              )}
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(suburbsByRegion[r.name] || []).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/computer-repairs-${s.slug}`}
                    data-testid={`suburb-card-${s.slug}`}
                    className="te-card p-5 group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{s.name}</span>
                      <ArrowRight size={14} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="te-overline text-[10px] mt-1">{s.postcode} · {r.name}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function SuburbDetail({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const s = suburbsBySlug[slug];
  if (!s) return <Navigate to="/suburbs" replace />;

  const focusService = services.find((x) => x.slug === s.focus) || services[0];
  const FocusIcon = iconMap[focusService.icon] || ShieldCheck;
  const neighbours = (suburbsByRegion[s.region] || []).filter((x) => x.slug !== s.slug).slice(0, 6);
  const faqs = getSuburbFaqs(s);

  // Canonical URL — keyword-rich pattern Google has been crawling via the sitemap.
  // The /suburbs/:slug route still works (and 301-redirects here), but THIS is the
  // self-canonical Google should index.
  const canonicalPath = `/computer-repairs-${s.slug}`;
  const canonicalUrl = `https://techemergency.com.au${canonicalPath}`;

  // Unique, suburb-specific meta description (first 155 chars indexed by Google).
  // We use the unique intro paragraph rather than the old templated string so each
  // suburb page differs from the next — directly fixes the duplicate-description
  // SEO problem flagged in the audit.
  const metaDescription = (() => {
    const lead = `Computer repairs ${s.name} ${s.postcode} — on-site, same day, $0 callout. `;
    const intro = (s.intro || "").replace(/\s+/g, " ").trim();
    const trimmed = intro.length > 130 ? intro.slice(0, 127) + "…" : intro;
    return (lead + trimmed).slice(0, 300);
  })();

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        name: `Tech Emergency — ${s.name}`,
        description: `Mobile on-site computer repairs, IT support and cyber security in ${s.name}, Adelaide ${s.postcode}.`,
        telephone: "+611800836390",
        url: canonicalUrl,
        areaServed: {
          "@type": "Place",
          name: s.name,
          address: {
            "@type": "PostalAddress",
            addressLocality: s.name,
            postalCode: s.postcode,
            addressRegion: "SA",
            addressCountry: "AU",
          },
        },
        priceRange: "$$",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "5.0",
          reviewCount: "61",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEO
        title={`Computer Repairs ${s.name} ${s.postcode} — On-Site, Same Day | Tech Emergency`}
        description={metaDescription}
        path={canonicalPath}
        jsonLd={jsonLd}
      />

      <section className="pt-5 sm:pt-10 pb-10 sm:pb-12 te-noise relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: "var(--te-primary)" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/suburbs" className="te-overline mb-3 sm:mb-5 inline-block hover:text-white">/ ← All suburbs</Link>
          <div className="te-overline mb-3">/ {s.region} · {s.postcode}</div>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-none mb-6">
            Computer Repairs<br />in {s.name}.
          </h1>
          <p className="text-zinc-300 max-w-2xl text-lg leading-relaxed mb-6 sm:mb-8">
            {s.intro}
          </p>

          {/* Landmark chips */}
          <div className="flex flex-wrap gap-2 mb-6 sm:mb-8" data-testid="suburb-landmarks">
            <span className="te-overline text-[10px] flex items-center mr-2">SERVICING:</span>
            {s.landmarks.map((lm) => (
              <span
                key={lm}
                className="px-2 py-1 border border-white/10 text-xs text-zinc-300"
                style={{ background: "var(--te-paper)" }}
              >
                {lm}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a href={PHONE_TEL} data-testid="suburb-call-btn" className="te-btn te-btn-primary">
              <Phone size={16} /> Call {PHONE}
            </a>
            <Link to="/pricing" data-testid="suburb-pricing-btn" className="te-btn te-btn-ghost">
              View Pricing — from $139 <ArrowRight size={14} />
            </Link>
            <Link to="/book" data-testid="suburb-book-btn" className="te-btn te-btn-ghost">
              Send an enquiry for {s.name}
            </Link>
          </div>
        </div>
      </section>

      {/* Common fixes — REAL local content (different per suburb) */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ What we commonly fix in {s.name}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {s.commonFixes.map((f) => (
              <div key={f} className="te-card p-5 flex items-start gap-3">
                <Check size={18} className="mt-1 flex-shrink-0" style={{ color: "var(--te-primary)" }} />
                <span className="text-zinc-200">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus service highlight */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="border border-white/10 p-8 md:p-10 grid md:grid-cols-2 gap-8 items-center"
            style={{ background: "var(--te-paper)" }}
          >
            <div>
              <div className="te-overline mb-3">/ A common call-out in {s.name}</div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center" style={{ background: "var(--te-primary)" }}>
                  <FocusIcon size={22} color="#fff" />
                </div>
                <h3 className="text-3xl font-bold tracking-tight">{focusService.title}</h3>
              </div>
              <p className="text-zinc-300 leading-relaxed mb-6">{focusService.long}</p>
              <Link
                to={`/services/${focusService.slug}`}
                className="te-btn te-btn-ghost"
                data-testid="suburb-focus-link"
              >
                Service details <ArrowRight size={14} />
              </Link>
            </div>
            <ul className="space-y-2">
              {focusService.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-zinc-300 text-sm">
                  <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--te-primary)" }} />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* All services available */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tighter mb-8">Home IT services available in {s.name}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "rgba(255,255,255,0.08)" }}>
            {services.filter((srv) => srv.audience === "home").map((srv) => {
              const Icon = iconMap[srv.icon] || ShieldCheck;
              const isFocus = srv.slug === s.focus;
              return (
                <Link
                  to={`/services/${srv.slug}`}
                  key={srv.slug}
                  className="p-6 te-card relative"
                  style={{ background: "var(--te-bg)" }}
                  data-testid={`suburb-service-${srv.slug}`}
                >
                  {isFocus && (
                    <span className="absolute top-3 right-3 te-overline text-[9px] px-2 py-0.5" style={{ background: "var(--te-primary)", color: "#fff" }}>
                      TOP IN {s.name.toUpperCase()}
                    </span>
                  )}
                  <Icon size={18} style={{ color: "var(--te-primary)" }} className="mb-3" />
                  <div className="font-semibold mb-1">{srv.title}</div>
                  <p className="text-sm text-zinc-400">{srv.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nearby suburbs (internal linking) */}
      {neighbours.length > 0 && (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="te-overline mb-4">/ Nearby suburbs in {s.region} Adelaide</div>
            <div className="flex flex-wrap gap-2">
              {neighbours.map((n) => (
                <Link
                  key={n.slug}
                  to={`/computer-repairs-${n.slug}`}
                  className="px-3 py-2 border border-white/10 text-sm hover:border-[var(--te-primary)] hover:text-white text-zinc-300 transition-colors"
                  data-testid={`nearby-${n.slug}`}
                >
                  {n.name} <span className="text-zinc-500">{n.postcode}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Also covered — short non-templated reminder */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          <p className="text-sm text-zinc-400 leading-relaxed">
            <span className="te-overline text-[10px] mr-2">ALSO COVERED IN {s.name.toUpperCase()}:</span>
            <Link to="/services/tech-support" className="text-zinc-200 hover:text-[var(--te-primary)] underline-offset-4 hover:underline">Apple ecosystem support (MacBook, iPhone, iPad, Apple Watch)</Link>
            {" · "}
            <Link to="/services/wifi-troubleshooting" className="text-zinc-200 hover:text-[var(--te-primary)] underline-offset-4 hover:underline">Home Wi-Fi &amp; internet troubleshooting</Link>
            {" · "}
            <Link to="/services/unifi-home-networking-adelaide" className="text-zinc-200 hover:text-[var(--te-primary)] underline-offset-4 hover:underline">Home UniFi networking</Link>
            {" · "}
            <Link to="/services/data-recovery" className="text-zinc-200 hover:text-[var(--te-primary)] underline-offset-4 hover:underline">Data backup &amp; recovery</Link>
            {" · "}
            <Link to="/services/anti-scamming-cyber-awareness" className="text-zinc-200 hover:text-[var(--te-primary)] underline-offset-4 hover:underline">Anti-scamming &amp; virus removal</Link>
            .
          </p>
        </div>
      </section>

      {/* Service limits & hours — consistent across every suburb */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-px" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="p-6" style={{ background: "var(--te-bg)" }} data-testid="suburb-hours">
              <div className="te-overline mb-3">/ Hours of service</div>
              <ul className="text-sm text-zinc-300 space-y-1.5">
                <li className="flex justify-between"><span>Monday – Saturday</span><span className="text-white font-medium">9:00 AM – 7:00 PM</span></li>
                <li className="flex justify-between"><span>Sunday</span><span className="text-zinc-500">Closed</span></li>
              </ul>
            </div>
            <div className="p-6" style={{ background: "var(--te-bg)" }} data-testid="suburb-exclusions">
              <div className="te-overline mb-3">/ A note on what we don&apos;t do</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Tech Emergency is a mobile home IT service — we do not perform <strong className="text-zinc-200">physical hardware repairs</strong> such as cracked phone or tablet screen replacements. Our work covers computer repairs, tech support, Wi-Fi troubleshooting, email solutions, anti-scam coaching, virus removal and data recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Suburb FAQs — also feeds FAQPage JSON-LD above for Google "People also ask" */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-6">/ Frequently asked — {s.name}</div>
          <div className="space-y-3" data-testid="suburb-faqs">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="te-card group"
                data-testid={`suburb-faq-${i}`}
              >
                <summary className="cursor-pointer p-5 flex items-start justify-between gap-4 list-none">
                  <h3 className="text-base sm:text-lg font-semibold tracking-tight text-white pr-4">
                    {f.q}
                  </h3>
                  <span
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center border border-white/15 text-lg leading-none transition-transform group-open:rotate-45"
                    style={{ color: "var(--te-primary)" }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <div className="px-5 pb-5 pt-0 text-zinc-300 leading-relaxed text-sm sm:text-base border-t border-white/5 mt-0">
                  <p className="pt-4">{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center te-card p-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-4">
            On call for {s.name} — <span style={{ color: "var(--te-primary)" }}>local &amp; ready to help.</span>
          </h2>
          <p className="text-zinc-400 mb-6">Mon–Sat, 9am–7pm. A real Adelaide-based technician.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={PHONE_TEL} className="te-btn te-btn-primary" data-testid="suburb-bottom-call-btn">
              <Phone size={16} /> {PHONE}
            </a>
            <Link to="/book" className="te-btn te-btn-ghost" data-testid="suburb-bottom-book-btn">Send an enquiry</Link>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Pulls the slug out of `/computer-repairs-{slug}` URLs (React Router v6 can't
 * bind a path param mid-segment) and renders the suburb detail page. Falls
 * back to the 404 page for unknown suburbs so the page-not-found copy still
 * appears (better than silently redirecting to /suburbs).
 */
export function SuburbRouteResolver() {
  const { pathname } = useLocation();
  // pathname is always "/computer-repairs-<slug>" here because of the route prefix.
  const slug = pathname.replace(/^\/computer-repairs-/, "").replace(/\/+$/, "");
  if (!slug || !suburbsBySlug[slug]) {
    // Defer to the global 404 by rendering it inline — keeps URLs intact.
    // Importing NotFound here would be circular; the cleanest path is to just
    // redirect to /suburbs which is also a valid landing.
    return <Navigate to="/suburbs" replace />;
  }
  return <SuburbDetail slug={slug} />;
}

/**
 * Redirect old /suburbs/:slug URLs to the SEO-canonical /computer-repairs-:slug.
 *
 * React Router can't issue a real 301 (it's client-side), so we do two things
 * on top of the client-side navigate to keep Googlebot happy:
 *   1. Set the canonical to point to the DESTINATION URL (not the shell's default
 *      "canonical=/"), so if Google indexes this URL at all, it credits the right one.
 *   2. Set <meta name="robots" content="noindex, follow"> so Google won't try to
 *      index the source URL and won't flag it as a "Redirect error" in GSC.
 *   3. Meta refresh fallback for the rare pre-JS crawler.
 *
 * The proper long-term fix is a CDN-level 301 (Cloudflare Bulk Redirect for
 * `/suburbs/*` → `/computer-repairs-$1`) — that gets set up in the Cloudflare
 * dashboard, not here.
 */
export function LegacySuburbRedirect() {
  const { slug } = useParams();
  const validSlug = slug && suburbsBySlug[slug] ? slug : null;
  const destPath = validSlug ? `/computer-repairs-${validSlug}` : "/suburbs";
  const destUrl = `https://techemergency.com.au${destPath}`;

  useEffect(() => {
    // Update the head tags directly — a real browser will follow the Navigate
    // below within a tick, but Googlebot may snapshot the head first.
    const setHead = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.attrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
    };
    setHead('link[rel="canonical"]', {
      tag: "link",
      attrs: { rel: "canonical" },
      set: { href: destUrl },
    });
    setHead('meta[name="robots"]', {
      tag: "meta",
      attrs: { name: "robots" },
      set: { content: "noindex, follow" },
    });
    // For the (rare) crawler that renders zero JS, a plain meta refresh is the
    // closest thing we can do to a 301 from a static HTML shell.
    setHead('meta[http-equiv="refresh"]', {
      tag: "meta",
      attrs: { "http-equiv": "refresh" },
      set: { content: `0; url=${destUrl}` },
    });
  }, [destUrl]);

  return <Navigate to={destPath} replace />;
}

